import { customerKycFields } from '../customer/customer-kyc.js';
import { fundingNavigationManifest as manifest } from '../generated/navigation.js';
export function fundingCheckoutTarget(event, method) {
    if (method &&
        !['wallet_checkout', 'hosted_checkout', 'card_checkout', 'bank_transfer_checkout'].includes(manifest.methods[method].flow))
        throw new Error('onramp_navigation_flow_invalid');
    return manifest.checkout.events[event];
}
export function fundingCheckoutBack(screen) {
    return manifest.checkout.screens[screen].back;
}
export function fundingContactBack(kind, step) {
    const screen = step === 'code' ? `${kind}_code` : step === 'consent' ? 'consent' : kind;
    return manifest.contact.back[screen];
}
/** Navigation contains no credentials, OTPs, quotes or authority to submit a payment. */
export function createFundingNavigation(origin = 'deposit', directPurchase = false) {
    let state = {
        page: origin,
        pickerCategory: 'money',
        checkout: null,
        operationId: null,
        attempted: false,
        receiptId: null,
        receiptOrigin: origin,
        revision: 0,
    };
    let deliveredId = null;
    const listeners = new Set();
    const update = (next) => {
        state = { ...state, ...next };
        for (const listener of listeners)
            listener();
    };
    const deposit = () => update({
        page: manifest.entry.events.new_deposit,
        checkout: manifest.checkout.events.change_method,
        operationId: null,
        attempted: false,
        receiptId: null,
        revision: state.revision + 1,
    });
    const withdraw = () => update({
        page: manifest.entry.events.new_withdraw,
        checkout: null,
        operationId: null,
        attempted: false,
        receiptId: null,
        revision: state.revision + 1,
    });
    const openReceipt = (id, origin) => update({ page: manifest.entry.events.open_receipt, receiptId: id, receiptOrigin: origin });
    return {
        getSnapshot: () => state,
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        transition: (event) => update({
            checkout: directPurchase && event === 'change_method'
                ? manifest.direct_token_purchase.change_method
                : fundingCheckoutTarget(event),
        }),
        trackOperation: (id) => update({ operationId: id }),
        markAttempt: () => update({ attempted: true }),
        observeDelivery: (id) => {
            if (id === state.operationId ||
                (state.page === 'receipt' && state.receiptOrigin === 'deposit' && id === state.receiptId))
                deliveredId = id;
        },
        openHistory: () => update({ page: manifest.entry.events.open_history, receiptId: null }),
        closeHistory: () => {
            if (state.page === 'history' ||
                (state.page === 'receipt' && state.receiptOrigin === 'history'))
                update({ page: origin, receiptId: null });
        },
        openReceipt,
        deposit,
        resumeDeposit: () => update({ page: 'deposit', receiptId: null }),
        openManualTransfer: () => update({
            page: manifest.entry.events.open_manual_transfer,
            pickerCategory: 'wallets',
            receiptId: null,
        }),
        openCryptoDeposit: () => update({
            page: manifest.entry.events.open_crypto_deposit,
            pickerCategory: 'wallets',
            receiptId: null,
        }),
        openConnectedWallet: () => update({
            page: manifest.entry.events.open_connected_wallet,
            pickerCategory: 'wallets',
            receiptId: null,
        }),
        setPickerCategory: (pickerCategory) => update({ pickerCategory }),
        withdraw,
        back: () => {
            if (state.page === 'manual_transfer' ||
                state.page === 'crypto_deposit' ||
                state.page === 'connected_wallet') {
                deposit();
                update({ pickerCategory: 'wallets' });
                return;
            }
            const target = state.page === 'receipt' ? manifest.entry.receipt_back[state.receiptOrigin] : origin;
            if (target === 'deposit' && state.page === 'history' && !state.attempted)
                update({ page: target, checkout: manifest.checkout.events.change_method, receiptId: null });
            else if (target === 'deposit')
                deposit();
            else if (target === 'withdraw')
                withdraw();
            else
                update({ page: target, receiptId: null });
        },
        restore: (retainUnsubmitted = false) => {
            if (state.page === 'connected_wallet') {
                deposit();
                return;
            }
            if (deliveredId &&
                (deliveredId === state.operationId ||
                    (state.receiptOrigin === 'deposit' && deliveredId === state.receiptId))) {
                deliveredId = null;
                deposit();
            }
            else if (state.page === 'deposit' &&
                state.operationId &&
                !state.attempted &&
                (state.checkout === 'payment' || state.checkout === 'review')) {
                // A live checkout still owns this reservation and its provider frame signature.
                if (!retainUnsubmitted)
                    deposit();
            }
            else if (state.page === 'deposit' && state.attempted && state.operationId)
                openReceipt(state.operationId, 'deposit');
            // Fresh discovery revalidates eligibility; persisted drafts choose the next safe step.
            else if (state.page === 'deposit' && state.checkout !== 'profile')
                update({ checkout: manifest.checkout.events.session_refreshed });
        },
    };
}
export function fundingKycScreen(verification) {
    if (verification.challenge)
        return manifest.kyc.screens.document_or_selfie_required;
    if (verification.status === 'active')
        return manifest.kyc.screens.verified_active;
    if (verification.status === 'not_created')
        return manifest.kyc.screens.onboarding_not_created;
    if (verification.requirements.phoneNumber?.status === 'incomplete')
        return manifest.kyc.screens.phone_required;
    if (verification.status === 'collecting' &&
        customerKycFields(verification).fields.length === 0 &&
        ['identityDocuments', 'selfie', 'proofOfAddress'].some((requirement) => verification.requirements[requirement]?.status === 'incomplete'))
        return manifest.kyc.screens.document_or_selfie_required;
    return manifest.kyc.screens.details_or_pending;
}
export function fundingPaymentScreen(method) {
    return manifest.methods[method].payment_screen;
}
